import React, { useState } from 'react';
import {
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
} from '@mui/material';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import { useNavigate } from 'react-router-dom';
import { useSessionQuery, useLogout } from '../../../features/auth';
import { ROUTES } from '../../utils';
import { AuthenticatedChangePasswordDialog } from '../../../features/auth/components/AuthenticatedChangePasswordDialog';

export const UserMenu: React.FC = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const { data: session } = useSessionQuery();
  const logoutMutation = useLogout();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Logout failed:', error);
    }
    handleMenuClose();
  };

  const handleOpenPasswordDialog = () => {
    setPasswordDialogOpen(true);
    handleMenuClose();
  };

  const getInitials = () => {
    if (!session?.user) return 'A';
    const first = session.user.firstName?.[0] || '';
    const last = session.user.lastName?.[0] || '';
    return `${first}${last}`.toUpperCase() || 'A';
  };

  return (
    <>
      <IconButton onClick={handleMenuOpen} size='small'>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.main',
            fontSize: '0.875rem',
          }}
        >
          {getInitials()}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant='subtitle2' fontWeight={600}>
            {session?.user?.firstName} {session?.user?.lastName}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {session?.user?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleOpenPasswordDialog}>
          <ListItemIcon>
            <LockResetRoundedIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText>Change password</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogout} disabled={logoutMutation.isPending}>
          <ListItemIcon>
            <LogoutRoundedIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', textAlign: 'center' }}>
            Version {__APP_VERSION__}
          </Typography>
        </Box>
      </Menu>
      <AuthenticatedChangePasswordDialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} />
    </>
  );
};
